import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { useRef, useState } from 'react';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9n41ju8mbnljin59tf0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '69视频',
    url: 'https://69video.com',
    description: '69视频汇聚丝瓜视频、蓝莓视频、糖心视频、黑桃视频等特色视频网站，提供丰富视频资源免费在线观看。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@69video.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <div className="min-h-screen bg-background-50 text-foreground-50">
        <Navbar />

        <main>
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]} />

          {/* Hero */}
          <section className="relative w-full overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20office%20space%20with%20warm%20lighting%2C%20creative%20team%20collaboration%20area%2C%20media%20company%20headquarters%2C%20sleek%20dark%20interior%20design%2C%20professional%20corporate%20atmosphere%2C%20cinematic%20aesthetic&width=1920&height=500&seq=about-hero-01&orientation=landscape"
                alt="69视频 — 关于我们"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-background-50"></div>
            </div>
            <div className="relative w-full px-4 md:px-6 lg:px-8 py-20 md:py-28">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-3xl md:text-5xl font-bold text-white">
                  关于<span className="text-amber-400">69视频</span>
                </h1>
                <p className="mt-4 text-base text-gray-300 leading-relaxed">
                  我们致力于打造中国领先的特色视频网站导航和内容聚合平台，为用户提供最全面的视频资源发现服务
                </p>
              </div>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-20">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
                品牌<span className="text-amber-400">介绍</span>
              </h2>
              <div className="space-y-6 text-sm md:text-base text-gray-300 leading-relaxed">
                <p>
                  <strong className="text-white">69视频</strong>成立于2020年，是国内领先的特色视频网站导航和影视内容聚合平台。我们汇聚了丝瓜视频、蓝莓视频、糖心视频、黑桃视频、妖精视频、桃花视频等六大特色视频平台，为用户提供超过12,000部优质视频资源的发现和观看服务。
                </p>
                <p>
                  在视频内容日益丰富的今天，用户面临的最大挑战不是"没得看"，而是"不知道看什么"。<strong className="text-white">69视频</strong>正是为解决这一痛点而生 — 我们通过精细化的分类体系和智能化的推荐算法，帮助用户在海量视频资源中快速找到自己感兴趣的内容。
                </p>
                <p>
                  我们的六大特色视频网站各具特色：<strong className="text-amber-400">丝瓜视频</strong>以高清流畅的观影体验著称，<strong className="text-amber-400">蓝莓视频</strong>主打精品内容和4K超清画质，<strong className="text-amber-400">糖心视频</strong>专注温馨治愈类内容，<strong className="text-amber-400">黑桃视频</strong>聚焦悬疑推理和科幻动作，<strong className="text-amber-400">妖精视频</strong>呈现奇幻魔幻题材，而<strong className="text-amber-400">桃花视频</strong>则深耕古装武侠和历史传奇。无论您喜欢什么类型的视频内容，都能在69视频找到心仪的选择。
                </p>
                <p>
                  平台每日更新超过500部视频资源，涵盖电影、电视剧、综艺、动漫、纪录片等多种类型。我们的编辑团队持续追踪最新的影视资讯和行业动态，为用户带来专业、及时的观影指南和内容推荐。
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div className="bg-background-50 rounded-xl p-6 border border-white/5">
                  <p className="text-3xl md:text-4xl font-bold text-amber-400 font-heading">2020</p>
                  <p className="text-xs text-gray-400 mt-1">创立年份</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-white/5">
                  <p className="text-3xl md:text-4xl font-bold text-amber-400 font-heading">6</p>
                  <p className="text-xs text-gray-400 mt-1">特色视频平台</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-white/5">
                  <p className="text-3xl md:text-4xl font-bold text-amber-400 font-heading">12K+</p>
                  <p className="text-xs text-gray-400 mt-1">视频资源</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-white/5">
                  <p className="text-3xl md:text-4xl font-bold text-amber-400 font-heading">500万+</p>
                  <p className="text-xs text-gray-400 mt-1">月活用户</p>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation / Values */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-20">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
                品牌<span className="text-amber-400">口碑</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-100 rounded-xl p-6 border border-white/5 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-400/10 flex items-center justify-center">
                    <i className="ri-star-fill text-2xl text-amber-400"></i>
                  </div>
                  <h3 className="font-semibold text-white mb-2">用户好评率 98%</h3>
                  <p className="text-sm text-gray-400">来自真实用户的积极反馈，我们珍视每一份信任和支持</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 border border-white/5 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-400/10 flex items-center justify-center">
                    <i className="ri-thumb-up-line text-2xl text-amber-400"></i>
                  </div>
                  <h3 className="font-semibold text-white mb-2">行业认可</h3>
                  <p className="text-sm text-gray-400">获得多项行业奖项和媒体推荐，视频导航领域标杆平台</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 border border-white/5 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-400/10 flex items-center justify-center">
                    <i className="ri-shake-hands-line text-2xl text-amber-400"></i>
                  </div>
                  <h3 className="font-semibold text-white mb-2">深度合作</h3>
                  <p className="text-sm text-gray-400">与多家知名视频平台建立深度合作关系，内容来源丰富可靠</p>
                </div>
              </div>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-100 rounded-xl p-6 border border-white/5">
                  <div className="flex items-start gap-3">
                    <i className="ri-double-quotes-l text-3xl text-amber-400/40 flex-shrink-0"></i>
                    <div>
                      <p className="text-sm text-gray-300 leading-relaxed italic">
                        "69视频是我每天必逛的视频导航网站，分类清晰、更新及时，帮我发现了超多好看的电影和剧集，强烈推荐给所有影迷朋友！"
                      </p>
                      <p className="mt-3 text-xs text-gray-500">— 影视爱好者 张先生</p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-100 rounded-xl p-6 border border-white/5">
                  <div className="flex items-start gap-3">
                    <i className="ri-double-quotes-l text-3xl text-amber-400/40 flex-shrink-0"></i>
                    <div>
                      <p className="text-sm text-gray-300 leading-relaxed italic">
                        "作为一个追剧达人，69视频的六大视频平台覆盖了我所有的观影需求，丝瓜视频的高清画质和黑桃视频的悬疑片库是我的最爱。"
                      </p>
                      <p className="mt-3 text-xs text-gray-500">— 追剧达人 李女士</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Our Platforms */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-16 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
                我们的<span className="text-amber-400">特色视频平台</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="bg-background-50 rounded-xl p-5 border border-white/5 hover:border-amber-400/20 transition-all text-center cursor-pointer group"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${cat.color}15` }}>
                      <i className={`${cat.icon} text-xl`} style={{ color: cat.color }}></i>
                    </div>
                    <h3 className="font-medium text-white text-sm group-hover:text-amber-400 transition-colors">{cat.name}</h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-20">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-4">
                联系<span className="text-amber-400">我们</span>
              </h2>
              <p className="text-sm text-gray-400 text-center mb-10">
                如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
                <div className="bg-background-100 rounded-xl p-5 border border-white/5 text-center">
                  <i className="ri-mail-line text-2xl text-amber-400 mb-2 block"></i>
                  <h3 className="font-medium text-white text-sm">电子邮箱</h3>
                  <p className="text-xs text-gray-400 mt-1">contact@69video.com</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 border border-white/5 text-center">
                  <i className="ri-phone-line text-2xl text-amber-400 mb-2 block"></i>
                  <h3 className="font-medium text-white text-sm">客服电话</h3>
                  <p className="text-xs text-gray-400 mt-1">400-123-4567</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 border border-white/5 text-center">
                  <i className="ri-map-pin-line text-2xl text-amber-400 mb-2 block"></i>
                  <h3 className="font-medium text-white text-sm">公司地址</h3>
                  <p className="text-xs text-gray-400 mt-1">北京市朝阳区科技园路88号</p>
                </div>
              </div>

              <form ref={formRef} onSubmit={handleSubmit} className="bg-background-100 rounded-xl p-6 md:p-8 border border-white/5" data-readdy-form="" id="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5" htmlFor="contact_name">姓名</label>
                    <input
                      type="text"
                      id="contact_name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:border-amber-400/50 focus:outline-none transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5" htmlFor="contact_email">邮箱</label>
                    <input
                      type="email"
                      id="contact_email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:border-amber-400/50 focus:outline-none transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-1.5" htmlFor="contact_subject">主题</label>
                  <input
                    type="text"
                    id="contact_subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:border-amber-400/50 focus:outline-none transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-1.5" htmlFor="contact_message">留言内容</label>
                  <textarea
                    id="contact_message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 bg-background-50 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:border-amber-400/50 focus:outline-none transition-colors resize-none"
                    placeholder="请输入您的留言内容（不超过500字）"
                  ></textarea>
                </div>

                {/* Honeypot */}
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />

                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg transition-all text-sm whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <p className="mt-4 text-sm text-green-400 text-center">留言发送成功！我们会尽快与您联系。</p>
                )}
                {formStatus === 'error' && (
                  <p className="mt-4 text-sm text-red-400 text-center">{formError}</p>
                )}
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}